import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  Typography,
  IconButton,
  Avatar,
  Chip,
  InputAdornment,
  TextField,
  Breadcrumbs,
  Link,
  CircularProgress,
  Snackbar,
  Alert,
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  ReceiptLong as ReceiptLongIcon,
  Search as SearchIcon,
  Sync as SyncIcon,
  Clear as ClearIcon,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import Sidebar from '../components/Sidebar';
import { useAuth } from '../contexts/AuthContext';
import tangoSyncService from '../services/tangoSyncService';
import Pagination from '../components/common/Pagination';

const Clients = () => {
  const navigate = useNavigate();
  const { logout } = useAuth();
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'success'
  });

  // Pagination states
  const [page, setPage] = useState(0);
  const [totalCount, setTotalCount] = useState(0);

  useEffect(() => {
    const delayDebounce = setTimeout(() => {
      fetchClients(page, searchTerm, filterStatus);
    }, 300);
    return () => clearTimeout(delayDebounce);
  }, [page, searchTerm, filterStatus]);

  const fetchClients = async (currentPage = page, search = searchTerm, status = filterStatus) => {
    try {
      setLoading(true);
      const pageParam = `page=${currentPage + 1}`;
      const searchParam = search ? `search=${encodeURIComponent(search)}` : '';
      
      let statusParams = '';
      if (status === 'active') {
        statusParams = 'is_active=true&credit_status=aprobado';
      } else if (status === 'debt') {
        statusParams = 'has_debt=true';
      } else if (status === 'blocked') {
        statusParams = 'is_blocked=true';
      }
      
      const queryParams = [pageParam, searchParam, statusParams].filter(Boolean).join('&');

      const response = await axios.get(`/api/clients/?${queryParams}`);
      if (response.data && response.data.results) {
        setClients(response.data.results);
        setTotalCount(response.data.count);
      } else {
        setClients(response.data || []);
        setTotalCount(response.data ? response.data.length : 0);
      }
    } catch (error) {
      console.error('Error fetching clients:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSync = async () => {
    try {
      setSyncing(true);
      const res = await tangoSyncService.syncClients();
      setSnackbar({
        open: true,
        message: res.message || 'Sincronización finalizada con éxito.',
        severity: 'success'
      });
      fetchClients(page, searchTerm, filterStatus);
    } catch (error) {
      console.error('Error syncing clients:', error);
      const errorMsg = error.response?.data?.message || 'Error al conectar con Tango ERP. Intente de nuevo.';
      setSnackbar({
        open: true,
        message: errorMsg,
        severity: 'error'
      });
    } finally {
      setSyncing(false);
    }
  };

  const handleLogout = async () => {
    await logout();
    navigate('/login');
  };

  const getInitials = (name) => {
    if (!name) return 'C';
    const words = name.split(' ');
    if (words.length >= 2) {
      return (words[0][0] + words[1][0]).toUpperCase();
    }
    return name.substring(0, 2).toUpperCase();
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'aprobado':
        return { bg: '#2e7d32', text: '#ffffff' };
      case 'revision':
        return { bg: '#ff9800', text: '#ffffff' };
      case 'bloqueado':
        return { bg: '#d32f2f', text: '#ffffff' };
      default:
        return { bg: '#9e9e9e', text: '#ffffff' };
    }
  };

  const getStatusLabel = (status) => {
    switch (status) {
      case 'aprobado':
        return 'Aprobado';
      case 'revision':
        return 'Revisión';
      case 'bloqueado':
        return 'Bloqueado';
      default:
        return status;
    }
  };

  const getPriceListLabel = (priceList) => {
    const labels = {
      mayorista_a: 'Mayorista A',
      mayorista_b: 'Mayorista B',
      premium: 'Premium',
      estandar: 'Estándar',
    };
    return labels[priceList] || priceList;
  };

  const CLIENT_TYPES = [
    { field: 'is_retail', label: 'Minorista', color: { bg: '#e3f2fd', color: '#1976d2', border: '#90caf9' } },
    { field: 'is_wholesale', label: 'Mayorista', color: { bg: '#fff3e0', color: '#f57c00', border: '#ffb74d' } },
  ];

  const handleToggleClientType = async (client, field, label) => {
    const newValue = !client[field];
    // Update optimista
    setClients((prev) => prev.map((c) => (c.id === client.id ? { ...c, [field]: newValue } : c)));
    try {
      await axios.patch(`/api/clients/${client.id}/`, { [field]: newValue });
      setSnackbar({
        open: true,
        message: `"${client.name}" ${newValue ? 'habilitado como' : 'deshabilitado como'} ${label}.`,
        severity: 'success',
      });
    } catch (error) {
      console.error('Error updating client type:', error);
      // Revertir
      setClients((prev) => prev.map((c) => (c.id === client.id ? { ...c, [field]: !newValue } : c)));
      setSnackbar({ open: true, message: 'No se pudo actualizar el tipo de cliente.', severity: 'error' });
    }
  };

  const renderClientTypeChips = (client) => (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
      {CLIENT_TYPES.map(({ field, label, color }) => {
        const active = !!client[field];
        return (
          <Chip
            key={field}
            label={label}
            size="small"
            onClick={() => handleToggleClientType(client, field, label)}
            sx={{
              cursor: 'pointer',
              fontWeight: 700,
              fontSize: '0.65rem',
              bgcolor: active ? color.bg : 'transparent',
              color: active ? color.color : '#9aa8b8',
              border: `1px solid ${active ? color.border : '#d0dbe7'}`,
              opacity: active ? 1 : 0.7,
              '&:hover': { bgcolor: active ? color.bg : 'rgba(0,0,0,0.04)' },
            }}
          />
        );
      })}
    </Box>
  );

  const filteredClients = clients;



  return (
    <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, minHeight: '100vh', bgcolor: '#f6f7f8' }}>
      <Sidebar onLogout={handleLogout} />
      <Box sx={{ flex: 1, p: { xs: 2, md: 4 }, pt: { xs: 7, md: 4 } }}>
        {/* Breadcrumbs */}
        <Breadcrumbs sx={{ mb: 3 }}>
          <Link color="inherit" href="#" sx={{ color: '#4e7397', fontSize: '0.875rem' }}>
            Inicio
          </Link>
          <Typography color="text.primary" sx={{ color: '#0e141b', fontSize: '0.875rem', fontWeight: 500 }}>
            Clientes
          </Typography>
        </Breadcrumbs>

        {/* Header */}
        <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, justifyContent: 'space-between', mb: 4, gap: 2 }}>
          <Box sx={{ maxWidth: '600px' }}>
            <Typography
              variant="h3"
              sx={{
                color: '#0e141b',
                fontSize: { xs: '1.875rem', md: '2.25rem' },
                fontWeight: 900,
                mb: 1,
                letterSpacing: '-0.033em',
              }}
            >
              Gestión de Clientes
            </Typography>
            <Typography sx={{ color: '#4e7397', fontSize: '1rem' }}>
              Administre la información, crédito y condiciones de sus clientes.
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'center' }}>
            <Button
              variant="outlined"
              startIcon={syncing ? <CircularProgress size={16} color="inherit" /> : <SyncIcon />}
              onClick={handleSync}
              disabled={syncing || loading}
              sx={{
                borderColor: '#800020',
                color: '#800020',
                '&:hover': { borderColor: '#600010', bgcolor: 'rgba(128,0,32,0.04)' },
                borderRadius: '0.5rem',
                px: 3,
                py: 1.25,
                fontWeight: 700,
                textTransform: 'none',
                whiteSpace: 'nowrap',
              }}
            >
              {syncing ? 'Sincronizando...' : 'Sincronizar Tango'}
            </Button>
          </Box>
        </Box>

        {/* Filters */}
        <Box sx={{ display: 'flex', flexDirection: { xs: 'column', lg: 'row' }, gap: 2, mb: 4, alignItems: { xs: 'stretch', lg: 'center' } }}>
          <TextField
            placeholder="Buscar cliente..."
            value={searchTerm}
            onChange={(e) => { setSearchTerm(e.target.value); setPage(0); }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon sx={{ color: '#4e7397', fontSize: '1.25rem' }} />
                </InputAdornment>
              ),
              endAdornment: searchTerm && (
                <InputAdornment position="end">
                  <IconButton
                    aria-label="clear search"
                    onClick={() => {
                      setSearchTerm('');
                      setPage(0);
                    }}
                    edge="end"
                    size="small"
                  >
                    <ClearIcon />
                  </IconButton>
                </InputAdornment>
              )
            }}
            sx={{
              flex: 1,
              '& .MuiOutlinedInput-root': {
                borderRadius: '0.75rem',
                backgroundColor: 'white',
                '& fieldset': { borderColor: '#d0dbe7' },
              },
            }}
          />
          <Box sx={{ display: 'flex', gap: 1, overflowX: 'auto', pb: 1, '&::-webkit-scrollbar': { height: '4px' }, '&::-webkit-scrollbar-thumb': { bgcolor: '#d0dbe7', borderRadius: '10px' } }}>
            {[
              { id: 'all', label: 'Todos', count: filterStatus === 'all' ? totalCount : undefined },
              { id: 'active', label: 'Activos', color: '#2e7d32', count: filterStatus === 'active' ? totalCount : undefined },
              { id: 'debt', label: 'Con Deuda', color: '#ff9800', count: filterStatus === 'debt' ? totalCount : undefined },
              { id: 'blocked', label: 'Bloqueados', color: '#d32f2f', count: filterStatus === 'blocked' ? totalCount : undefined },
            ].map((f) => (
              <Button
                key={f.id}
                variant={filterStatus === f.id ? 'contained' : 'outlined'}
                onClick={() => { setFilterStatus(f.id); setPage(0); }}
                sx={{
                  bgcolor: filterStatus === f.id ? '#0e141b' : 'white',
                  color: filterStatus === f.id ? 'white' : '#4e7397',
                  borderColor: '#d0dbe7',
                  borderRadius: '9999px',
                  px: 2,
                  py: 0.75,
                  textTransform: 'none',
                  fontSize: '0.75rem',
                  fontWeight: 700,
                  whiteSpace: 'nowrap',
                }}
              >
                {f.color && <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: f.color, mr: 1 }} />}
                {f.label}
                {f.count !== undefined && <Chip label={f.count} size="small" sx={{ ml: 1, height: 18, fontSize: '0.65rem', bgcolor: filterStatus === f.id ? 'rgba(255,255,255,0.2)' : '#f6f7f8', color: filterStatus === f.id ? 'white' : '#4e7397' }} />}
              </Button>
            ))}
          </Box>
        </Box>

        {/* Table / Cards */}
        <Box>
          {/* Desktop Table */}
          <TableContainer
            component={Paper}
            sx={{
              display: { xs: 'none', md: 'block' },
              borderRadius: '0.75rem',
              border: '1px solid #d0dbe7',
              boxShadow: 'none',
              overflow: 'hidden',
            }}
          >
            <Table>
              <TableHead>
                <TableRow sx={{ bgcolor: '#f6f7f8' }}>
                  <TableCell sx={{ color: '#4e7397', fontWeight: 600, fontSize: '0.75rem', textTransform: 'uppercase', py: 2 }}>Cód. Cliente</TableCell>
                  <TableCell sx={{ color: '#4e7397', fontWeight: 600, fontSize: '0.75rem', textTransform: 'uppercase' }}>Cliente / Razón Social</TableCell>
                  <TableCell sx={{ color: '#4e7397', fontWeight: 600, fontSize: '0.75rem', textTransform: 'uppercase' }}>Tipo</TableCell>
                  <TableCell sx={{ color: '#4e7397', fontWeight: 600, fontSize: '0.75rem', textTransform: 'uppercase' }}>CUIT</TableCell>
                  <TableCell sx={{ color: '#4e7397', fontWeight: 600, fontSize: '0.75rem', textTransform: 'uppercase' }}>Ubicación / Dirección</TableCell>
                  <TableCell sx={{ color: '#4e7397', fontWeight: 600, fontSize: '0.75rem', textTransform: 'uppercase' }}>Lista</TableCell>
                  <TableCell sx={{ color: '#4e7397', fontWeight: 600, fontSize: '0.75rem', textTransform: 'uppercase' }}>Cond. Venta (Tango)</TableCell>
                  <TableCell sx={{ color: '#4e7397', fontWeight: 600, fontSize: '0.75rem', textTransform: 'uppercase' }}>Crédito (Límite)</TableCell>
                  <TableCell sx={{ color: '#4e7397', fontWeight: 600, fontSize: '0.75rem', textTransform: 'uppercase' }}>Saldo (Tango / Local)</TableCell>
                  <TableCell sx={{ color: '#4e7397', fontWeight: 600, fontSize: '0.75rem', textTransform: 'uppercase' }}>Estado Tango</TableCell>
                  <TableCell align="right" sx={{ color: '#4e7397', fontWeight: 600, fontSize: '0.75rem', textTransform: 'uppercase' }}>Acciones</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {loading ? (
                  <TableRow><TableCell colSpan={10} align="center" sx={{ py: 4 }}>Cargando...</TableCell></TableRow>
                ) : filteredClients.length === 0 ? (
                  <TableRow><TableCell colSpan={10} align="center" sx={{ py: 4 }}>No hay clientes</TableCell></TableRow>
                ) : (
                  filteredClients.map((client) => {
                    const statusColor = getStatusColor(client.credit_status);
                    return (
                      <TableRow key={client.id} sx={{ '&:hover': { bgcolor: '#f6f7f8' } }}>
                        <TableCell sx={{ fontWeight: 700, color: '#0e141b', fontSize: '0.8125rem' }}>
                          {client.cod_cliente || `PG-${client.id}`}
                        </TableCell>
                        <TableCell>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                            <Avatar sx={{ bgcolor: '#800020', width: 32, height: 32, fontSize: '0.75rem', fontWeight: 700 }}>
                              {getInitials(client.name)}
                            </Avatar>
                            <Typography sx={{ color: '#0e141b', fontWeight: 700, fontSize: '0.875rem' }}>
                              {client.name}
                            </Typography>
                          </Box>
                        </TableCell>
                        <TableCell>
                          {renderClientTypeChips(client)}
                        </TableCell>
                        <TableCell sx={{ color: '#0e141b', fontSize: '0.8125rem', fontFamily: 'monospace' }}>
                          {client.cuit}
                        </TableCell>
                        <TableCell>
                          <Typography sx={{ color: '#0e141b', fontSize: '0.8125rem', fontWeight: 500 }}>
                            {client.delivery_address}
                          </Typography>
                          <Typography sx={{ color: '#4e7397', fontSize: '0.75rem' }}>
                            {client.city}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Chip label={getPriceListLabel(client.price_list)} size="small" sx={{ bgcolor: '#f6f7f8', fontSize: '0.7rem', fontWeight: 700 }} />
                        </TableCell>
                        <TableCell>
                          <Typography sx={{ color: '#0e141b', fontSize: '0.8125rem', fontWeight: 600 }}>
                            {client.tango_info ? (client.tango_info.condicion_venta === 1 ? 'Contado' : `Cta. Cte. (${client.tango_info.condicion_venta})`) : 'No Sincronizado'}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.25 }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                              <Box sx={{ width: 6, height: 6, borderRadius: '50%', bgcolor: statusColor.bg }} />
                              <Typography sx={{ fontSize: '0.8125rem', fontWeight: 600 }}>{getStatusLabel(client.credit_status)}</Typography>
                            </Box>
                            <Typography sx={{ color: '#4e7397', fontSize: '0.75rem' }}>
                              Límite: ${parseFloat(client.credit_limit || 0).toLocaleString('es-AR')}
                            </Typography>
                          </Box>
                        </TableCell>
                        <TableCell>
                          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.25 }}>
                            <Typography sx={{ fontWeight: 800, fontSize: '0.8125rem' }}>
                              Tango: ${client.tango_info ? parseFloat(client.tango_info.saldo_cc).toLocaleString('es-AR') : 'N/A'}
                            </Typography>
                            <Typography sx={{ color: '#4e7397', fontSize: '0.75rem' }}>
                              Local: ${parseFloat(client.current_balance || 0).toLocaleString('es-AR')}
                            </Typography>
                          </Box>
                        </TableCell>
                        <TableCell>
                          {client.tango_info ? (
                            client.tango_info.habilitado ? (
                              <Chip label="Habilitado" size="small" color="success" sx={{ fontSize: '0.7rem', fontWeight: 700 }} />
                            ) : (
                              <Chip label="Inhabilitado" size="small" color="error" sx={{ fontSize: '0.7rem', fontWeight: 700 }} />
                            )
                          ) : (
                            <Chip label="Sin Info" size="small" variant="outlined" sx={{ fontSize: '0.7rem', fontWeight: 700 }} />
                          )}
                        </TableCell>
                        <TableCell align="right">
                          <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1 }}>
                            <IconButton size="small" onClick={() => navigate(`/clients/${client.id}/account`)} sx={{ color: '#4e7397' }}><ReceiptLongIcon fontSize="small" /></IconButton>
                          </Box>
                        </TableCell>
                      </TableRow>
                    );
                  })
                )}
              </TableBody>
            </Table>
          </TableContainer>

          {/* Mobile Cards */}
          <Box sx={{ display: { xs: 'flex', md: 'none' }, flexDirection: 'column', gap: 2 }}>
            {loading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}><CircularProgress /></Box>
            ) : filteredClients.length === 0 ? (
              <Paper sx={{ p: 4, textAlign: 'center', borderRadius: '1rem', border: '1px solid #d0dbe7' }}><Typography sx={{ color: '#4e7397' }}>No se encontraron clientes</Typography></Paper>
            ) : (
              filteredClients.map((client) => {
                const statusColor = getStatusColor(client.credit_status);
                const isBlocked = client.credit_status === 'bloqueado' || !client.is_active;
                return (
                  <Paper
                    key={client.id}
                    sx={{
                      p: 2,
                      borderRadius: '1rem',
                      border: '1px solid #d0dbe7',
                      borderLeft: isBlocked ? '6px solid #d32f2f' : '1px solid #d0dbe7',
                    }}
                  >
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                        <Avatar sx={{ bgcolor: '#800020', width: 44, height: 44, fontSize: '1rem', fontWeight: 900 }}>{getInitials(client.name)}</Avatar>
                        <Box>
                          <Typography sx={{ fontWeight: 800, color: '#0e141b', fontSize: '1rem', lineHeight: 1.2 }}>{client.name}</Typography>
                          <Typography sx={{ color: '#4e7397', fontSize: '0.75rem' }}>{client.city} • {client.cuit || 'Sin CUIT'}</Typography>
                        </Box>
                      </Box>
                      <Box sx={{ display: 'flex', gap: 0.5 }}>
                        <IconButton size="small" onClick={() => navigate(`/clients/${client.id}/account`)} sx={{ color: '#4e7397', p: 0.5 }}><ReceiptLongIcon sx={{ fontSize: '1.25rem' }} /></IconButton>
                      </Box>
                    </Box>

                    <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2, mb: 2, p: 1.5, bgcolor: '#f6f7f8', borderRadius: '0.75rem' }}>
                      <Box>
                        <Typography sx={{ fontSize: '0.65rem', color: '#4e7397', textTransform: 'uppercase', fontWeight: 700 }}>Saldo</Typography>
                        <Typography sx={{ fontWeight: 900, color: client.current_balance > 0 ? '#d32f2f' : '#2e7d32', fontSize: '1.1rem' }}>${parseFloat(client.current_balance || 0).toLocaleString('es-AR')}</Typography>
                      </Box>
                      <Box>
                        <Typography sx={{ fontSize: '0.65rem', color: '#4e7397', textTransform: 'uppercase', fontWeight: 700 }}>Crédito</Typography>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 0.5 }}>
                          <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: statusColor.bg }} />
                          <Typography sx={{ fontWeight: 700, fontSize: '0.875rem' }}>{getStatusLabel(client.credit_status)}</Typography>
                        </Box>
                      </Box>
                    </Box>

                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                        <Chip label={getPriceListLabel(client.price_list)} size="small" sx={{ bgcolor: 'white', border: '1px solid #d0dbe7', fontWeight: 700, fontSize: '0.7rem' }} />
                        <Box sx={{ display: 'flex', gap: 0.5 }}>
                          {CLIENT_TYPES.map(({ field, label, color }) => {
                            const active = !!client[field];
                            return (
                              <Chip
                                key={field}
                                label={label}
                                size="small"
                                onClick={() => handleToggleClientType(client, field, label)}
                                sx={{
                                  cursor: 'pointer',
                                  fontWeight: 700,
                                  fontSize: '0.6rem',
                                  bgcolor: active ? color.bg : 'transparent',
                                  color: active ? color.color : '#9aa8b8',
                                  border: `1px solid ${active ? color.border : '#d0dbe7'}`,
                                  opacity: active ? 1 : 0.7,
                                  height: 20,
                                  '&:hover': { bgcolor: active ? color.bg : 'rgba(0,0,0,0.04)' },
                                }}
                              />
                            );
                          })}
                        </Box>
                      </Box>
                      <Button
                        variant="contained"
                        size="small"
                        onClick={() => navigate(`/clients/${client.id}`)}
                        sx={{ bgcolor: '#0e141b', textTransform: 'none', fontWeight: 700, borderRadius: '0.5rem', px: 2 }}
                      >
                        Ver Perfil
                      </Button>
                    </Box>
                  </Paper>
                );
              })
            )}
          </Box>
        </Box>

        {/* Pagination */}
        {totalCount > 0 && (
          <Pagination
            count={totalCount}
            page={page}
            rowsPerPage={20}
            onPageChange={(e, newPage) => setPage(newPage)}
          />
        )}
      </Box>

      {/* Toast Alert */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={5000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          severity={snackbar.severity}
          variant="filled"
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default Clients;
